import React from 'react';
import './App.css';
import * as d3 from "d3";
import { useRef, useEffect } from 'react';
import { useState } from 'react';

import { addDoc, collection } from "firebase/firestore";
import db from './firebase';
import { parseLangFromURL } from './utils/lang';
import { getStrings } from './i18n/strings';
import { fetchHpcDataAndFields } from './services/dataService';
import { getFooterTemplate, getHeaderTemplate } from './utils/pdfTemplates';

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Firebase is initialized in src/firebase.js

const FIRESTORE_COLLECTION_PDF_SEED = 'pdfs_data'
let chartIsAnimating = false; // Flag to track if the chart is currently animating

// Firestore instance imported from ./firebase

// Layout constants
const LAYOUT_CONFIG = {
  // Breakpoints for switching layouts
  BREAKPOINT_SCREEN_MD: 768,
  BREAKPOINT_SCREEN_XS: 320,
  
  // Node rectangle configuration
  rect: {
    xOffset: -1.5,    // rect x position relative to dy
    desktop: {
      width: 3,         // rect width relative to dy
      textX: 0,         // text x position (centered)
    },
    mobile: {
      width: 2.8,       // revert to stable width
      textX: -0.1,      // slight adjustment to center text
    }
  },
  
  // Link styling configuration
  links: {
    desktop: {
      strokeWidth: 8,
      arrowWidth: 2.5
    },
    mobile: {
      strokeWidth: 4,
      arrowWidth: 2.5
    }
  },
  
  // Locale-specific font sizing floors
  locale: {
    en: {
      normalizedFontFloor: 1.2,
      perNodeFontFloor: 1.35,
      perNodeFontFloorMobile: 1.20,
    },
    es: {
      normalizedFontFloor: 1.20,
      perNodeFontFloor: 1.35,
      perNodeFontFloorMobile: 1.20,
    }
  }
}

// Typography helpers (DRY): single source of truth for character width & budgets
const MOBILE_CHAR_WIDTH = 8
const DESKTOP_CHAR_WIDTH = 11
const getCharWidth = (isMobile) => (isMobile ? MOBILE_CHAR_WIDTH : DESKTOP_CHAR_WIDTH)
const getLabelAvailableWidth = (dy, isMobile) => (
  dy * (isMobile ? LAYOUT_CONFIG.rect.mobile.width : LAYOUT_CONFIG.rect.desktop.width) - 5
)
const getNodeLabelBudget = (dy, isMobile) => {
  const budget = Math.floor(getLabelAvailableWidth(dy, isMobile) / getCharWidth(isMobile))
  return Math.max(14, budget)
}

const color = d3.scaleSequential([0, 1], d3.interpolate('#14967f','#04435c'))

// horizontal bezier curve generator
const linkGen = d3.linkHorizontal().x(d => d.y).y(d => d.x)

// Language-agnostic data normalization for key values used in D3 logic
const TYPE_ALIASES = {
  NEXT_DEGREE: new Set(['Next Degree', 'Próximo grado']),
  JOB: new Set(['Job Opportunity', 'Oportunidad laboral']),
  EDU: new Set(['Educational Opportunity', 'Oportunidad educativa'])
}
const IGNORE_ALIASES = new Set(['Ignore', 'Ignorar'])
const CAREER_ALIASES = new Set(['Health', 'Salud'])


const isNextType = (t) => TYPE_ALIASES.NEXT_DEGREE.has(t)
const isJobType = (t) => TYPE_ALIASES.JOB.has(t)
const isEduType = (t) => TYPE_ALIASES.EDU.has(t)
const isIgnoreType = (t) => IGNORE_ALIASES.has(t)

// Normalize keys used for parent/child relationships
const normalizeKey = (s) => (s == null ? s : String(s).trim())

// Split text into two lines
const split_text = (text) => {
  if (!text) return [text]
  let words = text.split(' ')
  if (words.length === 1) return [text]
  let half = Math.ceil(words.length / 2)
  return [words.slice(0, half).join(' '), words.slice(half).join(' ')]
}

// Better wrapping tuned for mobile: respects available width (via dy)
// Uses DRY helpers above to avoid diverging budgets. Allows up to 3 lines.
const node_lines = (text, dy, isMobile) => {
  if (!text) return [text]
  // allow breaks on '/' and '-' by padding with spaces
  const pre = String(text).replace(/[/-]/g, ' $& ')
  const words = pre.split(/\s+/)
  if (words.length === 1) return [text]
  const maxCharsPerLine = getNodeLabelBudget(dy, isMobile)
  const slack = isMobile ? 7 : 0  // allow a little extra packing on early lines
  // Greedy build up to three lines (avoid breaking inside words unless necessary)
  const lines = ['','','']
  let idx = 0
  words.forEach((w) => {
    let token = w
    // If a single word exceeds the line budget, split across current and next line with a hyphen
    if (token.length > maxCharsPerLine) {
      const cut = Math.max(3, Math.min(maxCharsPerLine - 1, Math.floor(token.length / 2)))
      if (!lines[idx]) {
        lines[idx] = token.slice(0, cut) + '-'
        if (idx < 2) {
          lines[idx + 1] = token.slice(cut)
          idx = idx + 1
        }
        return
      }
    }
    const candidate = (lines[idx] ? lines[idx] + ' ' : '') + token
    const limit = idx < 2 ? (maxCharsPerLine + slack) : maxCharsPerLine
    if (candidate.length <= limit || idx === 2) {
      lines[idx] = candidate
    } else {
      idx = Math.min(idx + 1, 2)
      lines[idx] = token
    }
  })
  // Trim
  let out = lines.filter(Boolean).map(s => s.trim())
  if (out.length === 0) return [text]
  // Rebalance if very uneven
  if (out.length === 2 && Math.abs(out[0].length - out[1].length) > 6) {
    const from = out[0].length > out[1].length ? 0 : 1
    const parts = out[from].split(/\s+/)
    if (parts.length > 1) {
      const moved = parts.pop()
      out[from] = parts.join(' ')
      const to = from === 0 ? 1 : 0
      out[to] = (out[to] ? out[to] + ' ' : '') + moved
    }
  }
  // 3-line rebalance: iteratively move from 2->1 and 3->2 while respecting budget+slack
  if (out.length === 3) {
    for (let iter = 0; iter < 3; iter++) {
      let changed = false
      // try move one word from L2 to L1
      let w2 = out[1].split(/\s+/).filter(Boolean)
      if (w2.length) {
        const cand1 = (out[0] ? out[0] + ' ' : '') + w2[0]
        if (cand1.length <= maxCharsPerLine + slack) {
          out[0] = cand1
          w2.shift()
          changed = true
        }
      }
      out[1] = w2.join(' ')
      // try move one word from L3 to L2
      let w3 = out[2].split(/\s+/).filter(Boolean)
      if (w3.length) {
        const cand2 = (out[1] ? out[1] + ' ' : '') + w3[0]
        if (cand2.length <= maxCharsPerLine + slack) {
          out[1] = cand2
          w3.shift()
          changed = true
        }
      }
      out[2] = w3.join(' ')
      if (!changed) break
    }
  }
  // Keep at two lines; if still long, the post-measure shrink will ensure fit
  return out
}

const set_fontsize = (text, dy, isMobile) => {
  if (!text) return null

  const padding = 2
  const labelAvailableWidth = dy * (isMobile ? LAYOUT_CONFIG.rect.mobile.width : LAYOUT_CONFIG.rect.desktop.width) - padding
  
  // Calculate space needed for the text when wrapped (match rendering logic)
  const lines = (isMobile ? node_lines(text, dy, isMobile) : split_text(text))
  const maxLineLength = Math.max(...lines.map(line => line.length))
  const numLines = lines.length
  
  const baseSize = isMobile ? 2.2 : 2.4
  const charWidth = getCharWidth(isMobile)
  
  // Calculate space efficiency - how much of available width we're using
  const spaceNeeded = maxLineLength * charWidth
  const spaceEfficiency = spaceNeeded / labelAvailableWidth
  
  // Scale based on both space efficiency and dy
  const dyScale = Math.min(1, dy / 35)  // Scale down when dy is small
  const widthScale = spaceEfficiency > 0.8 ? 
    labelAvailableWidth / (spaceNeeded * 1.2) : 
    labelAvailableWidth / spaceNeeded
  
  // Simple line scaling that considers actual vertical space (less punitive on mobile)
  const lineScale = numLines > 1 ? Math.pow(isMobile ? 0.985 : 0.9, numLines - 1) : 1
  
  const reductionFactor = Math.min(widthScale * lineScale * dyScale, 1)
  
  // Only reduce from base size if needed
  if (reductionFactor >= 1) {
    const safety = isMobile ? 0.99 : 1.0
    return baseSize * safety
  }

  const safety = isMobile ? 0.99 : 1.0
  return (baseSize * reductionFactor * safety)
}

// Approximate fit check for a given em size vs available label width
const will_fit = (text, dy, isMobile, emSize) => {
  if (!text) return true
  const padding = 2
  const labelAvailableWidth = dy * (isMobile ? LAYOUT_CONFIG.rect.mobile.width : LAYOUT_CONFIG.rect.desktop.width) - padding
  const lines = isMobile ? node_lines(text, dy, isMobile) : split_text(text)
  const maxLineLength = Math.max(...lines.map(line => line.length))
  const baseSize = isMobile ? 2.2 : 2.4
  const charWidth = getCharWidth(isMobile)
  const scaledNeeded = maxLineLength * charWidth * (emSize / baseSize)
  return scaledNeeded <= labelAvailableWidth
}

// Binary search to find largest em that fits (using node_lines for mobile wrapping)
const max_fit_em = (text, dy, isMobile, maxEm, minEm = 0.8, iterations = 8) => {
  if (!text) return minEm
  let lo = minEm, hi = Math.max(minEm, maxEm)
  // Ensure hi fits by shrinking until it does or lo==hi
  for (let k = 0; k < 6 && hi > lo; k++) {
    if (!will_fit(text, dy, isMobile, hi)) hi = (hi + lo) / 2; else break;
  }
  for (let i = 0; i < iterations; i++) {
    const mid = (lo + hi) / 2
    if (will_fit(text, dy, isMobile, mid)) lo = mid; else hi = mid
  }
  return lo
}

// Word-aware wrapping by character budget per line
const wrap_text_by_chars = (text, maxChars) => {
  if (!text) return []
  const words = String(text).split(/\s+/)
  const lines = []
  let current = ''
  words.forEach(w => {
    if (!current.length) { current = w; return; }
    if ((current + ' ' + w).length <= maxChars) {
      current = current + ' ' + w
    } else {
      lines.push(current)
      current = w
    }
  })
  if (current.length) lines.push(current)
  return lines
}

// Format currency and numerical values ($ and ,)
const getCurrencyFormattedDisplayValue = (fieldName, value) => {
  if (fieldName === 'Median Hourly Pay')  {
    return `$${value.toLocaleString()}`
  }
  if (fieldName === 'Median Annual Pay')  {
    // Locale string to show commas separating thousands
    return `$${Math.round(value).toLocaleString()}`
  }
  return value.toLocaleString()
}

  /**
   * Normalize font sizes for tree nodes based on the font size of their 'Opportunity' property.
   * @param {object} hierarchyData - Data used to generate the tree layout.
   * @param {number} dy - Vertical spacing between each node.
   * @param {boolean} isMobile - Whether or not the tree is being rendered on a mobile screen.
   * @param {object} i18n - i18n object for locale detection
   * @returns {number} The normalized font size to use for tree nodes (em units need to be added afterwards).
   */
  const dataNormalizedFontSize = (hierarchyData, dy, isMobile, i18n) => {
  let normalizedFontSize = 1.2

  // Determine locale using i18n (simple heuristic)
  const isEs = !!(i18n && typeof i18n.loadingData === 'string' && i18n.loadingData.toLowerCase().includes('cargando'))
  const localeKey = isEs ? 'es' : 'en'
  const localeFloors = LAYOUT_CONFIG.locale[localeKey]

  // Use only Next Degree nodes to set the global baseline (top-level buckets)
  const candidates = (hierarchyData || []).filter(d => isNextType(d['Opportunity Type']))

  // Collect font sizes, ensuring they are valid numbers
  const nodeFontSizes = candidates
    .map(d => set_fontsize(d['Opportunity'], dy, isMobile))
    .filter(fs => !isNaN(fs) && fs > 0);

  // Extract the smallest valid font size among Next nodes
  const minFontSize = nodeFontSizes.length > 0 ? Math.min(...nodeFontSizes) : localeFloors.normalizedFontFloor;

  // Clamp to a locale-aware floor to avoid Spanish shrinking due to long labels
  normalizedFontSize = Math.max(minFontSize, localeFloors.normalizedFontFloor);

  return normalizedFontSize
}

// Create markers for arrowheads
const make_marker = (id, marker_color, isMobile) => {
  const aw = (isMobile ? LAYOUT_CONFIG.links.mobile.arrowWidth : LAYOUT_CONFIG.links.desktop.arrowWidth)
  d3.select('defs').append('marker')
    .attr('id', id)
    .attr('viewBox', [ 0, -aw/2, aw, aw])
    .attr('refX', 0)
    .attr('refY', 0)
    .attr('markerWidth', aw)
    .attr('markerHeight', aw)
    .attr('orient', 'auto-start-reverse')
    .append('path')
    .attr('d', `M 0 ${aw/2} L ${aw} 0 L 0 ${-aw/2} L ${aw/10} 0 z ` )
    .attr('stroke-width', 0.1)
    .attr('stroke', 'black')
    .attr('fill', marker_color)
}

const createTooltip = () => {
  let Tooltip = d3.select("#svgContainer")
      .append("div")
      .attr("class", "tooltip")
      .attr("id", "tooltip")
      .style("position", "absolute")
      .style("opacity", 0)
      .style("pointer-events", "none")
  return Tooltip
}

// MODIFIED: tooltipHtml(tooltipFields, item, labelMap, i18n)
const tooltipHtml = (tooltipFields, item, labelMap, i18n) => {
  //console.log(item)
  let html_string = `
    <div class="card mb-1">
    <h5 class="card-header">${item.Opportunity}</h5>
    <div class="card-body">
    `
  let link = tooltipFields.includes('Link') ? item.Link : null
  tooltipFields.forEach(field => {
    if (!item[field]) return
    if (field === 'Link') return 
    let displayValue = getCurrencyFormattedDisplayValue(field, item[field])
    const label = (labelMap && labelMap[field]) || field
    html_string += `<p class='mb-1'><strong>${label}:</strong> ${displayValue}</p>`
  })
  if (link && link !== '') {
    html_string += `<hr class="hr" /><a href="${link}" target="_blank" class="btn learnMoreButton">${i18n.learnMore}</a>`
  }
  html_string += `</div></div>` 
  return html_string
}

// MODIFIED: showTooltip(event, d, tooltipFields, labelMap, i18n)
const showTooltip = (event, d, tooltipFields, labelMap, i18n) => {
  if (chartIsAnimating) return;
  
  let Tooltip = d3.select("#tooltip")
  if (Tooltip.empty()) Tooltip = createTooltip()
  
  Tooltip
    .html(tooltipHtml(tooltipFields, d.data, labelMap, i18n)) // MODIFIED
    .classed("visible", true)
    .style("opacity", 1)
    .style("visibility", "visible")
  
  positionTooltip(event, d)
}

// Position tooltip
const positionTooltip = (event, d) => {
  const Tooltip = d3.select("#tooltip")
  if (!Tooltip.node()) return
  
  const isMobile = window.innerWidth < LAYOUT_CONFIG.BREAKPOINT_SCREEN_MD
  
  // Get dimensions
  const tooltipWidth = Tooltip.node().getBoundingClientRect().width
  const tooltipHeight = Tooltip.node().getBoundingClientRect().height
  
  // Calculate position relative to the viewport
  const rect = event.currentTarget.getBoundingClientRect()
  const left = rect.left + window.scrollX
  const top = rect.top + window.scrollY
  
  // Calculate maximum allowed width for tooltip
  const maxWidth = Math.max(left - 20, isMobile ? window.innerWidth * 0.8 : window.innerWidth * 0.5)
  
  // Set tooltip width if needed
  if (tooltipWidth > maxWidth) {
    Tooltip.style("width", maxWidth + "px")
  }
  
  // Calculate vertical position ensuring tooltip stays in viewport
  let tooltipTop = top - tooltipHeight/2
  const minTop = window.scrollY + 10 // Keep 10px from top of viewport
  const maxTop = window.scrollY + window.innerHeight - tooltipHeight - 10 // Keep 10px from bottom
  tooltipTop = Math.max(minTop, Math.min(tooltipTop, maxTop))
  
  // Position tooltip
  Tooltip
    .style("left", (left - tooltipWidth) + "px")
    .style("top", tooltipTop + "px")
}

const hideTooltip = () => {
  const Tooltip = d3.select("#tooltip")
  if (!Tooltip.empty()) {
    Tooltip
      .classed("visible", false)
      .style("opacity", 0)
      .style("visibility", "hidden")
  }
}

// Position nodes
const reposition_nodes = (d, source, width, height, dx, dy) => {
  const isMobile = width < LAYOUT_CONFIG.BREAKPOINT_SCREEN_MD
  const xFactor = 1.5
  const yFactor = isMobile ? 1.5 : 3

  let xstart = xFactor*dy
  let ystart = width - yFactor*dy

  if (isNextType(d.data['Opportunity Type'])) {
    d.y = source ? yFactor*dy : width/2
    d.x = xstart
    // Only try to access parent if it exists (i.e., not the root node)
    // and the node is not 'High School' (which has special root-like positioning initially)
    if (d.parent) {
      d.y = d.parent.y
      d.x = d.parent.x + 2*dy
    }
  } else if (isEduType(d.data['Opportunity Type'])) {
    let childIndex = d.parent.children.filter(c => isEduType(c.data['Opportunity Type'])).findIndex(c =>
      c.id === d.id)
    d.y = ystart
    //d.x0 = (childIndex)*100
    d.x = xstart + childIndex*1.1*dy
    //d.x = d.parent.x + childIndex*0.8*dy
    //d.y0 = d.parent.y0
  } else if (isJobType(d.data['Opportunity Type'])) {
    let childIndex = d.parent.children.filter(c => isJobType(c.data['Opportunity Type'])).findIndex(c => c.id === d.id)
    //let xstart = -d.parent.children.filter(c => c.data['Opportunity Type']==='Job Opportunity').length*0.8*dy - 0.8*dy
    //ystart += ystart/3 + d.parent.children.filter(c => c.data['Opportunity Type'] === 'Educational Opportunity').length*0.8*dy //+ 0.8*dy
    d.y =  2*ystart
    d.x = xstart + childIndex*0.8*dy
    //d.x = d.parent.x + childIndex*0.8*dy // + 1.5*dy + arrowWidth*strokeWidth + 1
  }
  return d
}

// MODIFIED: update(source, rootNode, tree, width, height, dx, dy, normalizedFontSize, tooltipFields, labelMap, i18n)
const update = (source, rootNode, tree, width, height, dx, dy, normalizedFontSize, tooltipFields, labelMap, i18n) => {
  console.log('Updating nodes')
  console.log('Source Node:', source)
  console.log('Root Node:', rootNode)
  
  const isMobile = width < LAYOUT_CONFIG.BREAKPOINT_SCREEN_MD
  const svg = d3.select("svg")
  // Locale floors for per-node font sizing
  const isEsLocale = !!(i18n && typeof i18n.loadingData === 'string' && i18n.loadingData.toLowerCase().includes('cargando'))
  const localeFloors = LAYOUT_CONFIG.locale[isEsLocale ? 'es' : 'en']

  // Compute a single font size per node so both text lines match.
  const computeNodeFontSize = (d) => {
    // Next Degree nodes use the normalized base
    if (isNextType(d.data['Opportunity Type'])) return normalizedFontSize;
    const fullText = d.id.includes('>') ? d.id.split('>')[1] : d.id
    const candidate = set_fontsize(fullText, dy, isMobile) || normalizedFontSize
    if (isMobile) {
      // Mobile: search for the largest size that fits, cap at normalized for safety
      const floor = localeFloors.perNodeFontFloorMobile || 1.0
      const cap = normalizedFontSize
      let fs = max_fit_em(fullText, dy, isMobile, cap, floor, 10)
      if (fs < floor && will_fit(fullText, dy, isMobile, floor)) fs = floor
      return fs
    }
    // Desktop: apply locale floor for readability, cap to prevent oversized text
    const cap = normalizedFontSize * 1.15
    return Math.min(Math.max(candidate, localeFloors.perNodeFontFloor), cap)
  }

  // If the source node has the children hidden, show them
  if (source && !source.children) source.children = source._children

  // get direct descendants (not all descendants)
  let nodes = rootNode.descendants().reverse();
  let links = rootNode.links()
  //console.log('Nodes:', nodes)

  // Filter node and links to only include those that are children of source or Degree nodes
  if (source) {
    nodes = nodes.filter(n => 
      !isJobType(n.data['Opportunity Type']) && 
      ( (n.parent && n.parent.id === source.id) || isNextType(n.data['Opportunity Type']) )
    )
    links = links.filter(l => l.source.id === source.id )
  } else {
    nodes = nodes.filter(n => isNextType(n.data['Opportunity Type']))
    links = []
  }
  
  tree(rootNode);
  rootNode.descendants().forEach((d, i) => {
    d = reposition_nodes(d, source, width, height, dx, dy)
    // Compute and store a single font size per node for consistent multi-line sizing
    d._fs = computeNodeFontSize(d)
  });
  //console.log('Root Node:', rootNode)

  // find top most and bottom most leaf positions
  let left = rootNode;
  let right = rootNode;
  rootNode.eachBefore(node => {
    if ( !nodes.map(n => n.id).includes(node.id) ) return
    if (node.x < left.x)  left = node ;
    if (node.x > right.x) right = node;
  });
  //console.log('Root Node:', rootNode)
  // recompute svg height
  height = right.x - left.x + 2.5 * dy;

  // transition svg size and viewbox
  const transition = svg.transition()
  .duration(1000)
  .attr("viewBox", [0, 0, width, height])
  .tween("resize", window.ResizeObserver ? null : () => () => svg.dispatch("toggle"))
  .on("start", () => { chartIsAnimating = true; }) // Set flag to true when transition starts
  .on("end", () => { chartIsAnimating = false; }); // Set flag to false when transition ends

  const gradientEnd = width - 3*dy - dx    

  links = links.filter(l => {
    if (isNextType(l.target.data['Opportunity Type'])) return false
    if (isJobType(l.target.data['Opportunity Type'])) return false
    if (isEduType(l.target.data['Opportunity Type'])) return true
    return false
   })
  //console.log('Links:', links)


  // Assign link target ids to links as data. Update with transitions on enter and exit
  const gLink = svg.select(".links")
  gLink.selectAll("path")
    .data(links, d => d.target.id)
    .join(
      enter => enter.append("path")
        .attr("fill", "none")
        .attr("stroke", 'url(#color-gradient)')
        .attr("stroke-opacity", 1)
        .each(d => make_marker(d.target.id, color(d.target.y/gradientEnd), isMobile))
        .attr("marker-end", d => `url("#${d.target.id}")`)
        //.attr("display", "none") // Getting rid of arrow/links for now
        .attr("stroke-width", d => {
          const sw = isMobile ? LAYOUT_CONFIG.links.mobile.strokeWidth : LAYOUT_CONFIG.links.desktop.strokeWidth
          return isNextType(d.source.data['Opportunity Type']) ? sw : sw / 2
        })
        .attr("d", d => {
          if (source) return linkGen({ source: { x: source.x0, y: source.y0 }, target: { x: source.x0, y: source.y0 } }) 
          return linkGen({ source: { x: d.source.x, y: d.source.y }, target: { x: d.target.x, y: d.target.y } })
         })
        .call(enter => enter.transition(transition)
          .attr("d", d => {
            const origin = { x: d.source.x, y: d.source.y }
            //const origin = { x: d.source.x, y: d.source.y }
            //const target = { x: d.target.x, y: d.target.y }
            const target = {
              x: isNextType(d.target.data['Opportunity Type']) ? d.target.x - dy/2 - (isMobile ? LAYOUT_CONFIG.links.mobile.arrowWidth : LAYOUT_CONFIG.links.desktop.arrowWidth)*(isMobile ? LAYOUT_CONFIG.links.mobile.strokeWidth : LAYOUT_CONFIG.links.desktop.strokeWidth) : d.target.x  , 
              y: isNextType(d.target.data['Opportunity Type']) ? d.target.y : d.target.y + dy * (isMobile ? LAYOUT_CONFIG.rect.xOffset : LAYOUT_CONFIG.rect.xOffset) - (isMobile ? LAYOUT_CONFIG.links.mobile.arrowWidth : LAYOUT_CONFIG.links.desktop.arrowWidth)*(isMobile ? LAYOUT_CONFIG.links.mobile.strokeWidth : LAYOUT_CONFIG.links.desktop.strokeWidth) 
            }
            return linkGen({ source: origin, target: target })
          })
        ),
      update => update
        .call(update => update.transition(transition)
          //.attr("d", linkGen)
          //.attr("marker-end", "url(#arrow)")
          .attr("d", d => {
            const origin = { x: d.source.x, y: d.source.y }
            //const target = { x: d.target.x, y: d.target.y }          
            const target = {
               x: isNextType(d.target.data['Opportunity Type']) ? d.target.x - dy/2 - (isMobile ? LAYOUT_CONFIG.links.mobile.arrowWidth : LAYOUT_CONFIG.links.desktop.arrowWidth)*(isMobile ? LAYOUT_CONFIG.links.mobile.strokeWidth : LAYOUT_CONFIG.links.desktop.strokeWidth) : d.target.x ,//+ 1.5*dy + arrowWidth*strokeWidth + 1
               y: isNextType(d.target.data['Opportunity Type']) ? d.target.y : d.target.y + dy * (isMobile ? LAYOUT_CONFIG.rect.xOffset : LAYOUT_CONFIG.rect.xOffset) - (isMobile ? LAYOUT_CONFIG.links.mobile.arrowWidth : LAYOUT_CONFIG.links.desktop.arrowWidth)*(isMobile ? LAYOUT_CONFIG.links.mobile.strokeWidth : LAYOUT_CONFIG.links.desktop.strokeWidth) 
            }
            return linkGen({ source: origin, target: target });
          })
        ),
      exit => exit
        .call(exit => exit.transition(transition) // Transition exiting nodes to the parent's new position.  
          .remove()
          .attr("d", d => {
            const o = { x: d.source.x, y: d.source.y }
            //const o = source ? { x: source.x, y: source.y } : { x: rootNode.x, y: rootNode.y }
            return linkGen({ source: o, target: o });
          })
        )
    )

    ///Add text to links
    const textedLinks = [];
    const nextDegreeLink = rootNode.links().find(l => isNextType(l.target.data['Opportunity Type']));
    if (nextDegreeLink) {
      textedLinks.push(nextDegreeLink);
    }

    if (links.length) {
      const educationalOpportunityLink = links.find(l => isEduType(l.target.data['Opportunity Type']));
      if (educationalOpportunityLink) {
        textedLinks.push(educationalOpportunityLink);
      }
    }
    
    gLink.selectAll('text')
      .data(textedLinks, d => d.target.id)
      .join(
        enter => enter.insert('text', 'path')
          .classed('link-text', true)
          .attr("x", d => d.target.data['Opportunity Type'] === 'Next Degree' ? rootNode.y : d.source.y0)
          .attr("y", d => d.target.data['Opportunity Type'] === 'Next Degree' ? (rootNode.x - dy) : d.source.x0)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .attr("font-family", "Sofia Pro Regular")
          .each(function(d){
            const label = d.target.data['Opportunity Type'] === 'Next Degree'
              ? i18n.linkLabelSelectDegreeLevel
              : (isJobType(d.target.data['Opportunity Type'])
                  ? i18n.linkLabelSelectJob
                  : i18n.linkLabelSelectAnyOpportunities)
            const maxChars = isMobile ? (window.innerWidth < 360 ? 16 : 22) : 36
            const lines = wrap_text_by_chars(label, maxChars)
            const sel = d3.select(this)
            sel.text("")
            const lh = 1.1
            const start = -((lines.length - 1) * lh) / 2
            const tx = parseFloat(sel.attr('x')) || 0
            lines.forEach((ln, i) => {
              sel.append('tspan')
                .attr('x', tx)
                .attr('dy', (i === 0 ? `${start}em` : `${lh}em`))
                .text(ln)
            })
          })
          // .style("font-size", function(d){
          //   const label = isNextType(d.target.data['Opportunity Type']) ? i18n.linkLabelSelectDegreeLevel : (isJobType(d.target.data['Opportunity Type']) ? i18n.linkLabelSelectJob : i18n.linkLabelSelectAnyOpportunities)
          //   const maxChars = isMobile ? (window.innerWidth < 360 ? 16 : 22) : 36
          //   const lines = wrap_text_by_chars(label, maxChars)
          //   // Slightly larger on mobile: multi-line 1.0em, single-line 1.12em
          //   return isMobile ? (lines.length > 1 ? "1.0em" : "1.12em") : "1.05em"
          // })
          .attr("fill", d => source ? color(d.target.y/gradientEnd) : color(0) )
          .attr("stroke-width", 0.1)
          .attr("opacity", 1)
          .call(enter => enter.transition(transition)
            //.attr("opacity", 1)
            .attr("x", d => d.target.data['Opportunity Type'] === 'Next Degree' ? rootNode.y : d.target.y)
            .attr("y", d => d.target.data['Opportunity Type'] === 'Next Degree' ? rootNode.x - dy : rootNode.x - dy)
            .selection()
            .each(function(d){
              const newX = d.target.data['Opportunity Type'] === 'Next Degree' ? rootNode.y : d.target.y
              d3.select(this).selectAll('tspan').transition(transition).attr('x', newX)
            })
          ),
        update => update
          .call(update => update.transition(transition)
          //.attr("opacity", 1)
            .attr("x", d => d.target.data['Opportunity Type'] === 'Next Degree' ? rootNode.y : d.target.y)
            .attr("y", d => d.target.data['Opportunity Type'] === 'Next Degree' ? rootNode.x - dy : rootNode.x - dy)
            .selection()
            .each(function(d){
              const newX = d.target.data['Opportunity Type'] === 'Next Degree' ? rootNode.y : d.target.y
              d3.select(this).selectAll('tspan').transition(transition).attr('x', newX)
            })
          ),
          exit => exit
            .call(exit => exit.transition(transition)
              .remove()   // Transition exiting nodes to the parent's new position.
              .attr("transform", d => `translate(${-d.source.y},${d.source.x})` )
              .attr("opacity", 0)
          )
      )
    

    // Assign node ids as data. Update with transitions on enter and exit 
    const gNode = svg.select(".nodes")
    const gNodes = gNode.selectAll('g')
      .data(nodes, d => d.id)
    gNodes.join(
      enter => enter.append('g')
        .attr("cursor", "pointer") //d => d.data['Opportunity Type']==='Next Degree' ? "pointer" : "default")
        .attr("pointer-events", "all") //d => d.data['Opportunity Type']==='Next Degree' ? "all" : "none")
        .attr("transform", d => source ? `translate(${source.y0},${source.x0})` : `translate(${d.y},${d.x})`)
        .attr("fill-opacity", 0)
        .attr("stroke-opacity", 0)
        .classed('degree', d => isNextType(d.data['Opportunity Type']))
        .classed('job', d => isJobType(d.data['Opportunity Type']))
        .classed('edop', d => isEduType(d.data['Opportunity Type']))
        .each(d => { d._fs = computeNodeFontSize(d) })
        .call(enter => enter.append("rect")
          .attr('x', d => dy * LAYOUT_CONFIG.rect.xOffset)
          .attr('y', d => -dy/2)
          .attr('width', d => dy * (isMobile ? LAYOUT_CONFIG.rect.mobile.width : LAYOUT_CONFIG.rect.desktop.width))
          .attr('height', d => dy)
          .attr("rx", d => isNextType(d.data['Opportunity Type']) ? 0 : dy/3) //d.data['Opportunity Type']==='Educational Opportunity' ? dy/5 : dy/3)
          .attr("stroke-width", d => isNextType(d.data['Opportunity Type']) ? 2 : 1)
          .attr('stroke', d => source ? color(d.y/gradientEnd) : color(0))
          .attr('fill', '#f7f7f9')
          //.attr("fill", d => d.id === source.id ? color(d.y/gradientEnd) : '#f7f7f9')
          //.classed('selected', d => d.id === source.id)
          //.attr('fill', '#f7f7f9')
        )
        .call(enter => enter.append("text")
          .attr("x", d => dy * (LAYOUT_CONFIG.rect.xOffset + ((isMobile ? LAYOUT_CONFIG.rect.mobile.width : LAYOUT_CONFIG.rect.desktop.width) / 2)))
          .attr("y", 0)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .attr("font-family", "Sofia Pro Regular")
          .attr('fill', d => source ? color(d.y/gradientEnd) : color(0) )
          .attr('stroke', d => source ? color(d.y/gradientEnd) : color(0) )
          .attr('stroke-width', 0.25)
          .style("font-size", d => `${d._fs}em`)
          .each(function(d) {
            const label = d.id.includes('>') ? d.id.split('>')[1] : d.id
            // DRY: use the same wrapper/measurement logic everywhere
            const lines = isMobile ? node_lines(label, dy, isMobile) : split_text(label)
            const sel = d3.select(this)
            sel.text("")
            const lh = 1.1
            const start = -((lines.length - 1) * lh) / 2
            const tx = parseFloat(sel.attr('x')) || 0
            lines.forEach((ln, i) => {
              sel.append('tspan')
                .attr('x', tx)
                .attr('dy', (i === 0 ? `${start}em` : `${lh}em`))
                .text(ln)
            })
            // Post-measure: if overflow, shrink proportionally by width/height to fit
            try {
              const allowedW = dy * (isMobile ? LAYOUT_CONFIG.rect.mobile.width : LAYOUT_CONFIG.rect.desktop.width) - 6
              const allowedH = dy - 6
              const bbox = sel.node().getBBox()
              if (bbox && (bbox.width > allowedW || bbox.height > allowedH)) {
                const factorW = allowedW / bbox.width
                const factorH = allowedH / bbox.height
                const factor = Math.min(factorW, factorH)
                const newEm = Math.max(d._fs * factor * 0.98, isMobile ? 0.9 : 1.0)
                sel.style('font-size', `${newEm}em`)
              }
            } catch (_) {}
          })
        )
        .call(enter => enter.append("text")
          .style("display", "none")
        )
        .call(enter => enter.transition(transition)
          .attr("transform", d => `translate(${d.y},${d.x})`)
          .attr("fill-opacity", 1)
          .attr("stroke-opacity", 1)
        )
        .on("click", function(event, d) {         // recursively update on click event,  toggle switch for turning children on and off
            //console.log('Selected Node:', d3.select('.selected').data()[0] && d3.select('.selected').data()[0].id === d.id)
            const g = d3.select(this)
            const rectSel = g.select('rect')
            if (rectSel.empty()) return
            if(!isNextType(d.data['Opportunity Type'])) {
              //addToPla(d.data)
              return
            }
            d.children = d.children > 1 ? d._children.filter(c => isNextType(c.data['Opportunity Type'])) : d._children 
            if (d.depth === 0) { 
              rootNode.children = d.children
            } else {
              rootNode.find(node => node.id === d.id).children = d.children 
            }
            // Stash the old positions for transition.
            rootNode.eachBefore(d => {
              // position before next transition equal to current position
              d.x0 = d.x;
              d.y0 = d.y;
            });
            d3.selectAll('rect')
              .attr('stroke-width', 2)
              .attr('fill', '#f7f7f9' )
              .attr("filter", 'none')
            d3.selectAll('text').transition()
              .duration(100)
              .attr('fill', d => source ? color(d.y/gradientEnd) : color(0) )
              .attr('stroke', d => source ? color(d.y/gradientEnd) : color(0) )
            const selectedDatum = d3.select('.selected').data()[0]
            if (selectedDatum && selectedDatum.id === d.id) {
              d3.selectAll('rect').classed('selected', false)
              update(null, rootNode, tree, width, height, dx, dy, normalizedFontSize, tooltipFields, labelMap, i18n) // MODIFIED
            } else {
              d3.selectAll('rect').classed('selected', false)
              g.select('rect').classed('selected', true)
                .transition()
                .duration(100)
                .attr('stroke-width', 3)
                .attr('fill', d => source ? color(d.y/gradientEnd) : color(0))
              g.selectAll('text').transition()
                .duration(100)
                .attr('fill', 'white' )
                .attr('stroke', 'white' )
              update(d, rootNode, tree, width, height, dx, dy, normalizedFontSize, tooltipFields, labelMap, i18n); // MODIFIED
            } 
        })
        .on("mouseenter", function(event, d) {
          const g = d3.select(this)
          const rectSel = g.select('rect')
          if (rectSel.empty()) return
          rectSel.transition()
            .duration(100)
            .attr('stroke-width', 3)
            .attr('fill', d => source ? color(d.y/gradientEnd) : color(0) )
            .attr("filter", "url(#dropshadow)")
          g.selectAll('text').transition()
            .duration(100)
            .attr('fill', 'white' )
            .attr('stroke', 'white' )
          !isNextType(d.data['Opportunity Type']) && showTooltip(event, d, tooltipFields, labelMap, i18n) // MODIFIED
        })
        .on("mouseleave", function(event, d) {
          const g = d3.select(this)
          const rectSel = g.select('rect')
          if (rectSel.empty()) return
          rectSel.transition()
            .duration(100)
            .attr("filter", "none")
          !isNextType(d.data['Opportunity Type']) && hideTooltip()
          if (rectSel.classed('selected')) return
          rectSel.transition()
            .duration(100)
            .attr('stroke-width', d => isNextType(d.data['Opportunity Type']) ? 2 : 1)
            .attr("fill", '#f7f7f9')
          g.selectAll('text').transition()
            .duration(100)
            .attr('fill', d => source ? color(d.y/gradientEnd) : color(0))
            .attr('stroke',d => source ? color(d.y/gradientEnd) : color(0))
        }),
      update => update
        .call(update => update.transition(transition)
          .attr("transform", d => `translate(${d.y},${d.x})`)
          .attr("fill-opacity", 1)
          .attr("stroke-opacity", 1)
        ),
      exit => exit
        .call(exit => exit.transition(transition)
          .remove()   // Transition exiting nodes to the parent's new position.
          .attr("transform", d => `translate(${d.parent.y},${d.parent.x})` )
          .attr("fill-opacity", 0)
          .attr("stroke-opacity", 0)
        )
    )
  // Stash the old positions for transition.
  rootNode.eachBefore(d => {
    // position before next transition equal to current position
    d.x0 = d.x;
    d.y0 = d.y;
  });
}


// MODIFIED: drawChart(data, svgContainerRef, viewSize, tooltipFields, labelMap, i18n)
const drawChart = (data, svgContainerRef, viewSize, tooltipFields, labelMap, i18n) => {
  console.log('Drawing chart')

  const width = viewSize.width - 12  // 12 is the x-padding
  const isMobile = width < LAYOUT_CONFIG.BREAKPOINT_SCREEN_MD
  const isExtraSmall = width < LAYOUT_CONFIG.BREAKPOINT_SCREEN_XS
  const nextDegreeData = data.hierarchyData.filter(item => isNextType(item['Opportunity Type']))
  const height = isExtraSmall ? nextDegreeData.length * 168 :
               isMobile ? nextDegreeData.length * 192 : 
               nextDegreeData.length * 320

  const dx = width/3
  // Keep boxes from becoming too small by enforcing a minimum dy floor
  const dyRaw = height/(2*8)
  const dyFloor = isExtraSmall ? 36 : (isMobile ? 42 : 80) // tune as needed
  const dy = Math.max(dyRaw, dyFloor)
  const gradientEnd = width - 3*dy - dx

  console.log('Width:', width, 'Height:', height, 'dx:', dx, 'dy:', dy)

  const tree = d3.tree().nodeSize([dx, dy])

  let svg = d3.select("svg")

  if (svg.empty()) {
    console.log('No svg element found creating...')
    svg = d3.select(svgContainerRef.current).append("svg")
      .attr("viewBox", [0, 0, width, height/2])
      .style("font", "10px sans-serif")
      .style("user-select", "none")
  } else {
    svg.selectAll('*').remove()
  }

  // Add color gradient
  svg.append("linearGradient")
    .attr("id", "color-gradient")
    .attr("gradientUnits", "userSpaceOnUse")
     .attr("x1", width/2.5)
     .attr("y1", 0)
     .attr("x2", gradientEnd)
     .attr("y2", 0)
  .selectAll("stop")
    .data(d3.ticks(0, 1, 10))
  .join("stop")
    .attr("offset", d => d)
    .attr("stop-color", color.interpolator());

  const filter = svg.append("filter")
      .attr("id", "dropshadow")
  filter.append("feGaussianBlur")
      .attr("in", "SourceAlpha")
      .attr("stdDeviation", 4)
      .attr("result", "blur")
  filter.append("feOffset")
      .attr("in", "blur")
      .attr("dx", 2)
      .attr("dy", 2)
      .attr("result", "offsetBlur");
  const feMerge = filter.append("feMerge")
  feMerge.append("feMergeNode")
      .attr("in", "offsetBlur")
  feMerge.append("feMergeNode")
      .attr("in", "SourceGraphic")

  // Add defs for arrowhead markers
  svg.append('defs')
  
  // group links
  svg.append("g")
    .classed('links', true)
    //.attr("cursor", "pointer")
    //.attr("pointer-events", "all")

  // group nodes
  svg.append('g')
    .classed('nodes', true)
    //.attr("cursor", "pointer")
    //.attr("pointer-events", "all")

  // Filter by selected career using alias set and exclude ignored types
  let hierarchyData = data.hierarchyData.filter(r => r.Career == null || CAREER_ALIASES.has(normalizeKey(r.Career)))
  hierarchyData = hierarchyData.filter(r => !isIgnoreType(normalizeKey(r['Opportunity Type'])))
  // Sanitize keys used by stratify and alias checks
  hierarchyData = hierarchyData.map(d => ({
    ...d,
    Career: normalizeKey(d.Career),
    Degree: normalizeKey(d.Degree),
    Opportunity: normalizeKey(d.Opportunity),
    'Opportunity Type': normalizeKey(d['Opportunity Type'])
  }))
  // Split comma-separated Degree values into multiple rows (except Next nodes)
  hierarchyData = hierarchyData.flatMap(d => {
    if (!isNextType(d['Opportunity Type']) && d.Degree && d.Degree.includes(',')) {
      return d.Degree.split(',').map(part => ({ ...d, Degree: normalizeKey(part) }))
    }
    return d
  })
  // Derive degrees ONLY from non-Next rows (prevents stray categories like 'Graduate School')
  let degrees = [...new Set(
    hierarchyData
      .filter(d => !isNextType(d['Opportunity Type']))
      .map(d => d.Degree)
      .filter(v => v !== undefined && v !== null && v !== '')
  )]

  // Insert a localized root Next node if missing (original behavior uses 'High School')
  const ROOT_CANDIDATES = ['High School', 'Escuela secundaria']
  const existingNexts = new Set(hierarchyData.filter(d => isNextType(d['Opportunity Type'])).map(d => d.Opportunity))
  const rootLabel = ROOT_CANDIDATES.find(r => existingNexts.has(r) || degrees.includes(r)) || ROOT_CANDIDATES[0]
  if (!existingNexts.has(rootLabel)) {
    hierarchyData.push({ Career: null, Degree: '', 'Opportunity Type': 'Next Degree', Opportunity: rootLabel })
  }

  // Keep non-Next nodes. Keep Next nodes if root-like (Degree null/'') OR their Opportunity is a recognized Degree
  hierarchyData = hierarchyData.filter(d => {
    if (!isNextType(d['Opportunity Type'])) return true
    return (d.Degree === null || d.Degree === '') || degrees.includes(d['Opportunity'])
  })

  // Ensure the manually added root node has Degree: null to be a root
  const hsNodeIndex = hierarchyData.findIndex(d => isNextType(d['Opportunity Type']) && d.Opportunity === rootLabel)
  if (hsNodeIndex !== -1 && hierarchyData[hsNodeIndex].Degree === '') {
    hierarchyData[hsNodeIndex].Degree = null
  }

  // Create missing Next nodes for any degrees without a matching Next node (prevents stratify 'missing')
  const nextOpportunities = new Set(hierarchyData.filter(d => isNextType(d['Opportunity Type'])).map(d => d.Opportunity))
  const missingDegrees = degrees.filter(deg => deg !== undefined && deg !== null && deg !== '' && !nextOpportunities.has(deg))
  missingDegrees.forEach(deg => {
    hierarchyData.push({ Career: null, Degree: rootLabel, 'Opportunity Type': 'Next Degree', Opportunity: deg })
  })
  
  // As a last resort (e.g., if there are no Next nodes at all), do nothing; chart will short-circuit later.
  // Keep non-'Next Degree' types.
  // Keep 'Next Degree' types: root-like OR Opportunity listed in degrees (original behavior)
  hierarchyData = hierarchyData.filter(d => {
    if (!isNextType(d['Opportunity Type'])) {
      return true; // Keep all non-'Next Degree' nodes
    }
    return (d.Degree === null || d.Degree === '') || degrees.includes(d['Opportunity']);
  });
  // Ensure the manually added 'High School' node has Degree: null to be a root.
  // This step is crucial if the 'High School' node was initially added with Degree: ''
  // and needs to be the ultimate root for d3.stratify.
  // If a root Next node exists but has empty string Degree, set to null for d3.stratify
  const rootFixIndex = hierarchyData.findIndex(d => isNextType(d['Opportunity Type']) && (d.Degree === ''))
  if (rootFixIndex !== -1) {
    hierarchyData[rootFixIndex].Degree = null;
  }
  console.log('Hierarchy Data:', hierarchyData)
  const normalizedFontSize = dataNormalizedFontSize(hierarchyData, dy, isMobile, i18n)

  if (!hierarchyData || hierarchyData.length === 0) {
    console.warn("No data available to draw the chart after filtering. Skipping chart draw.");
    // Optionally, clear the SVG if it's not empty from a previous render
    const svg = d3.select(svgContainerRef.current).select("svg");
    if (!svg.empty()) {
      svg.selectAll('*').remove();
    }
    return;
  }

  const stratify = () => d3.stratify()
    .id(d => {
      let id = undefined
      if (isNextType(d['Opportunity Type'])) id = d['Opportunity'] 
      else if ( isJobType(d['Opportunity Type'])) id = d.Degree + '>' + d['Opportunity']
      else if ( isEduType(d['Opportunity Type'])) id = d.Degree + '>' + d['Opportunity']
      return id
     })
    .parentId(d => d.Degree )

  let rootData
  try {
    rootData = stratify()(hierarchyData)
  } catch (err) {
    const msg = (err && err.message) || ''
    const m = msg.match(/missing:\s*(.*)$/i)
    if (m && m[1]) {
      const missing = normalizeKey(m[1])
      console.warn(`[stratify] Missing parent "${missing}". Synthesizing Next node and retrying once.`)
      hierarchyData.push({ Career: null, Degree: null, 'Opportunity Type': 'Next Degree', Opportunity: missing })
      try {
        rootData = stratify()(hierarchyData)
      } catch (err2) {
        console.error('[stratify] Retry failed:', err2)
        throw err2
      }
    } else {
      throw err
    }
  }
    
  // Wrap data in tree attributes and methods
  const rootNode = tree(rootData)
  console.log('Root Node:', rootNode)

  // Reposition nodes, this has to be done for all 
  // nodes before hiding their children
  rootNode.descendants().forEach(d => {
    d = reposition_nodes(d, null, width, height, dx, dy)
  });
  // add id and child copy for each node
  
  // stash children for transitions
  rootNode.descendants().forEach(n => {
    n._children = n.children 
  })

  // assign rootNode node position
  rootNode.x0 = 0;
  rootNode.y0 = 0;

  // update chart
  //let selectedDegree = 'High School' 
  //let selectedNode = rootNode.find(node => node.id === selectedDegree)
  update(null, rootNode, tree, width, height, dx, dy, normalizedFontSize, tooltipFields, labelMap, i18n) // MODIFIED
}

const Viz = ({ addToPlan, data, tooltipFields, labelMap, i18n}) => {
  const svgContainerRef = useRef(null);
  const [preventRedraw, setPreventRedraw] = useState(false)
  const [viewSize, setViewSize] = useState({ width: window.innerWidth , height: window.innerHeight })
  
  const handleResize = () => {
    setViewSize({ width: window.innerWidth, height: window.innerHeight })
  }

  useEffect(() => {
    if (svgContainerRef.current && !preventRedraw && data && data.hierarchyData && data.hierarchyData.length > 0 && tooltipFields && tooltipFields.length > 0) { // MODIFIED: Check for data and tooltipFields
      drawChart(data, svgContainerRef, viewSize, tooltipFields, labelMap, i18n) // MODIFIED
    }
  }, [viewSize, data, tooltipFields, labelMap, i18n, preventRedraw]); // MODIFIED: Add data and tooltipFields to dependency array

  useEffect(() => {
    window.addEventListener('resize', handleResize)
      
    // cleanup this component
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleClick = (event) => {
    // Support clicks on any child (rect, text, tspan) by walking up to the bound <g>
    const groupEl = event.target.closest && event.target.closest('g.edop, g.job');
    if (!groupEl) return;
    const node = d3.select(groupEl);
    const datum = node.datum();
    if (!datum || !datum.data) return;
    setPreventRedraw(true);
    addToPlan(datum.data);
  }

  return (
    <div className="container-fluid py-2" id="svgContainer" onClick={handleClick} ref={svgContainerRef} data-testid="svg-container-testid"></div>
  )
}

function App() {
  const planRef = useRef(null);
  const [currentPlan, setCurrentPlan] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastText, setToastText] = useState('');

  // NEW STATE VARIABLES
  const [hpcData, setHpcData] = useState([]);
  const [hpcFields, setHpcFields] = useState(null);
  const [cardFields, setCardFields] = useState([]);
  const [tooltipFields, setTooltipFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [labelMap, setLabelMap] = useState({});
  const [lang] = useState(parseLangFromURL());
  const [i18n, setI18n] = useState(getStrings(parseLangFromURL()));

  // NEW useEffect for data fetching (language-aware)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const { hpcData: docsData, fieldsData } = await fetchHpcDataAndFields(db, lang);
        setHpcData(docsData || []);
        setHpcFields(fieldsData || null);

        if (fieldsData) {
          try { console.log('[fetchData] fieldsData:', fieldsData); } catch(_) {}
          const newLabelMap = fieldsData['labels'] || {};
          const reverseMap = Object.fromEntries(Object.entries(newLabelMap).map(([en, es]) => [es, en]));
          const normalizeFields = (arr) =>
            (arr || []).map(f => (f && typeof f === 'string' ? f.trim() : f))
                      .map(f => (newLabelMap[f] ? f : (reverseMap[f] || f)));
          const originalCardList = (fieldsData['Card Columns'] || []).map(f => (f && typeof f === 'string' ? f.trim() : f));
          const originalTooltipList = (fieldsData['Tooltip Columns'] || []).map(f => (f && typeof f === 'string' ? f.trim() : f));

          // Attempt index-based mapping via All Columns (ES -> EN) for stable keys
          let nextCardFields, nextTooltipFields;
          let enFields = null;
          try {
            const res = await fetchHpcDataAndFields(db, 'en');
            enFields = res && res.fieldsData ? res.fieldsData : null;
          } catch(_) {}
          const esAll = (fieldsData && fieldsData['All Columns'] ? fieldsData['All Columns'] : []).map(f => (typeof f === 'string' ? f.trim() : f));
          const enAll = (enFields && enFields['All Columns'] ? enFields['All Columns'] : []).map(f => (typeof f === 'string' ? f.trim() : f));
          const mapUsingAll = (arr) => (arr || []).map(name => {
            const n = (typeof name === 'string' ? name.trim() : name);
            const idx = esAll.findIndex(x => x === n);
            if (idx >= 0 && idx < enAll.length) return enAll[idx];
            // fallback to label-based reverse map
            return (newLabelMap[n] ? n : (reverseMap[n] || n));
          });
          if (esAll.length && enAll.length && esAll.length === enAll.length) {
            nextCardFields = mapUsingAll(originalCardList);
            nextTooltipFields = mapUsingAll(originalTooltipList);
          } else {
            nextCardFields = normalizeFields(originalCardList);
            nextTooltipFields = normalizeFields(originalTooltipList);
          }

          // Heuristic: if no sample items have any values for these fields (and labels missing), fallback to English fields doc
          const sampleItems = (docsData || []).slice(0, Math.min(10, (docsData || []).length));
          const hasAnyFieldHit = (fields) => sampleItems.some(itm => fields.some(f => itm && itm[f] != null && itm[f] !== ''));
          try { console.log('[fetchData] normalized cardFields:', nextCardFields, 'tooltipFields:', nextTooltipFields); } catch(_) {}
          try { console.log('[fetchData] sample item keys:', sampleItems[0] ? Object.keys(sampleItems[0]) : []); } catch(_) {}
          if (!hasAnyFieldHit(nextCardFields) || !hasAnyFieldHit(nextTooltipFields)) {
            try {
              const { fieldsData: enFields } = await fetchHpcDataAndFields(db, 'en');
              if (enFields) {
                nextCardFields = enFields['Card Columns'] || [];
                nextTooltipFields = enFields['Tooltip Columns'] || [];
              }
            } catch (e) {
              console.warn('Fallback to English fields failed:', e);
            }
          }

          // Build a derived label map from the original (possibly Spanish) lists
          const derivedLabelMap = {};
          originalCardList.forEach((orig, idx) => {
            const key = nextCardFields[idx];
            if (key && typeof orig === 'string' && orig && orig !== key) {
              derivedLabelMap[key] = orig;
            }
          });
          originalTooltipList.forEach((orig, idx) => {
            const key = nextTooltipFields[idx];
            if (key && typeof orig === 'string' && orig && orig !== key) {
              derivedLabelMap[key] = orig;
            }
          });
          // Build en->es label map from All Columns if both lists are present
          const labelFromAll = {};
          if (enAll.length && esAll.length && enAll.length === esAll.length) {
            enAll.forEach((enKey, i) => {
              const esLabel = esAll[i];
              if (enKey && esLabel && enKey !== esLabel) labelFromAll[enKey] = esLabel;
            });
          }
          const mergedLabelMap = { ...labelFromAll, ...derivedLabelMap, ...newLabelMap }; // All Columns + derived + explicit labels
          try { console.log('[fetchData] mergedLabelMap:', mergedLabelMap); } catch(_) {}

          setCardFields(nextCardFields);
          setTooltipFields(nextTooltipFields);
          setLabelMap(mergedLabelMap);
        } else {
          setCardFields([]);
          setTooltipFields([]);
          setLabelMap({});
          console.warn("Fields data is null or undefined after fetching.");
        }

        setI18n(getStrings(lang));
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(i18n.errorLoading || "Failed to load data. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);


  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailClick = () => {
    setEmailInput('');
    setEmailError('');
    setShowEmailModal(true);
  };

  const handleEmailSubmit = async () => {
    if (!validateEmail(emailInput)) {
      setEmailError(i18n.invalidEmail);
      return;
    }
    setShowEmailModal(false);
    await handleEmail(emailInput);
  };

  const showToastMessage = (text) => {
    setToastText(text)
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }

  const handleEmail = async (email) => {
    console.log('Handling email')
    try {
      const lcLang = (lang || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
      const outputFileName = `HealthCareerPath-${lcLang}-${email}.pdf`;

      let degrees = [...new Set(currentPlan.map(i => i.Degree))]

      // Precompute localized fields for each plan item (use cardFields order, fallback to tooltipFields)
      const fieldsOrder = (cardFields && cardFields.length ? cardFields : (tooltipFields || [])).filter(Boolean)
      const currentPlanForPdf = currentPlan.map(item => {
        const Fields = fieldsOrder
          .filter(f => item && item[f] != null && item[f] !== '')
          .map(f => ({
            key: f,
            label: (labelMap && labelMap[f]) || f,
            value: getCurrencyFormattedDisplayValue(f, item[f])
          }))
        return { ...item, Fields }
      })

      const dataToStore = {
        currentPlan: currentPlanForPdf,
        degrees: degrees,
        created: new Date().toISOString(),
        destinationEmail: email,
        lang: lcLang,
        // expose UI labels to the PDF template so it can render translated titles and field names
        i18n,
        labels: labelMap,
        cardFields: fieldsOrder,
        _pdfplum_config: {
          outputFileName: outputFileName,
          adjustHeightToFit: false,
          chromiumPdfOptions: {
            format: "Letter",
            printBackground: true,
            displayHeaderFooter: true,
            headerTemplate: getHeaderTemplate(),
            footerTemplate: getFooterTemplate(),          
            margin: {top: "0in", right: "1in", bottom: "0.5in", left: "1in"}
          }
        }
      };
      const docRef = await addDoc(collection(db, FIRESTORE_COLLECTION_PDF_SEED), dataToStore);
      console.log("Document written with ID: ", docRef.id);
      showToastMessage(i18n.toastEmailSent)
    } catch (error) {
      console.error("Error saving plan: ", error);
    }
  }

  const addToPlan = (item) => {
    console.log('Adding to plan:', item)
    showToastMessage(i18n.toastOpportunityAdded)
    setCurrentPlan([...currentPlan, item])
  } 

  const handleClear = () => {
    showToastMessage(i18n.toastPlanCleared)
    setCurrentPlan([])
  }

  //Remove single planItem from plan
  const removeFromPlan = (itemToRemove) => {
    showToastMessage(i18n.toastOpportunityRemoved)
    setCurrentPlan(currentPlan.filter(item => item !== itemToRemove));
  }

  // MODIFIED: planItemCard uses cardFields from state
  const planItemCard = (item, index) => {
    return (
      <div className="card mb-1" key={index}>
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="m-0">{item.Opportunity}</h5>
          <button 
            type="button" 
            className="btn-close" 
            aria-label="Remove from plan"
            title='Remove from plan'
            onClick={(e) => {
              removeFromPlan(item);
              e.target.blur();
            }}
          ></button>
        </div>
        <div className="card-body">
          {cardFields.map((field, idx) => { // MODIFIED: use cardFields from state
            if (item[field] && field !== 'Link'){
              let displayValue = getCurrencyFormattedDisplayValue(field, item[field])
              const label = (labelMap && labelMap[field]) || field
              return <p className='mb-1' key={idx}><strong>{label}:</strong> {displayValue}</p>
            }
            return null
          })}
          {cardFields.includes('Link') && item['Link'] && // MODIFIED: use cardFields from state and check item['Link']
           <div>
            <hr className="hr" />
            <a href={item['Link']} target="_blank" rel='noreferrer' className="btn learnMoreButton">{i18n.learnMore}</a>
           </div>
          }    
        </div>
      </div>
    )
  }


  const renderPlanItems = (currentPlan, planRef) => {
    let degrees = [...new Set(currentPlan.map(i => i.Degree))]
    if (currentPlan.length) return(
      <div id="plan" className='container-fluid mb-5' ref={planRef} >
        <div className="container plan-header text-center">
          <h1 className='plan-title'>{i18n.yourPathway}</h1>
        </div>
        <div className="container plan-footer d-flex justify-content-center my-3">
          <button type="button" className="btn emailButton mx-2" onClick={handleEmailClick}>{i18n.emailIt}</button>
          <button type="button" className="btn clearButton mx-2" onClick={handleClear}>{i18n.clear}</button>
        </div>
        <div className="plan-body row">
          {
            degrees.map((degree, index) => (
              <div className="col-md-3" key={index}>
                <h3 className='text-center'>{degree}</h3>
                {currentPlan.filter(item => item.Degree === degree).map((item, idx) => ( // MODIFIED: use idx for key
                  planItemCard(item, idx)  
                ))}
              </div>
            ))
          }
        </div>
      </div>
    ) 
  }

  const renderEmailModal = () => (
    <>
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          cursor: 'pointer',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw'
        }}
      ></div>
      <div 
        className="modal show" 
        tabIndex="-1" 
        role="dialog" 
        style={{ 
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          width: '100vw'
        }}
        onClick={() => setShowEmailModal(false)}
      >
        <div 
          className="modal-dialog" 
          onClick={e => e.stopPropagation()}
          style={{
            margin: '2rem auto',
            maxWidth: '32rem',
            width: '90%',
            transform: 'translate(0, 0)'
          }}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title card-header justify-content-center">{i18n.emailYourPathway}</h5>
              <button type="button" className="btn-close" onClick={() => setShowEmailModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <input
                  type="email"
                  className={`form-control ${emailError ? 'is-invalid' : ''}`}
                  placeholder={i18n.emailPlaceholder}
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setEmailError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleEmailSubmit();
                    }
                  }}
                />
                {emailError && <div className="invalid-feedback">{emailError}</div>}
              </div>
            </div>
            <div className="modal-modal-footer mb-2">
              <button type="button" className="btn sendButton mx-2" onClick={handleEmailSubmit}>{i18n.send}</button>
              <button type="button" className="btn cancelButton mx-2" onClick={() => setShowEmailModal(false)}>{i18n.cancel}</button>
            </div>
          </div>
        </div>
      </div>
    </>
  )

  const renderToast = () => (
    <div 
      className="alert alert-success d-flex align-items-center"
      style={{
        position: 'fixed',
        top: '5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        padding: '0.5rem 1rem',
        margin: 0,
        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
        animation: 'slideFromTop 3s ease-in-out'
      }}
      role="alert"
    >
      <i className="bi bi-check-circle-fill me-2"></i>
      {toastText}
    </div>
  )

  // NEW: Conditional rendering based on loading/error state
  if (loading) {
    return <div className="container text-center mt-5"><h2>{i18n.loadingData}</h2></div>;
  }

  if (error) {
    return <div className="container text-center mt-5 alert alert-danger"><h2>{i18n.error}</h2><p>{error}</p></div>;
  }
  
  if (!hpcData.length || !hpcFields) {
     return <div className="container text-center mt-5 alert alert-warning"><h2>{i18n.noDataTitle}</h2><p>{i18n.noDataMessage}</p></div>;
  }

  return (
    <>    
      {/* MODIFIED: Pass hpcData, tooltipFields, labelMap and i18n to Viz */}
      <Viz data={{ hierarchyData: hpcData }} addToPlan={addToPlan} tooltipFields={tooltipFields} labelMap={labelMap} i18n={i18n} />
      {renderPlanItems(currentPlan, planRef)}
      {showEmailModal && renderEmailModal()}
      {showToast && renderToast()}
    </>
  );
}

export default App;
